import { toastError } from '@/lib/toast-error';
import { trpc } from '@/lib/trpc';
import type {
  ApprovalFilters,
  ApprovalStatus,
  QuipteamsKit,
} from '../../../shared/types';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export type { QuipteamsKit };

/** Lo que acepta approvals.getApprovals: un solo status y sin `search` (ApprovalFilters compartido es más ancho). */
export type ApprovalQueryFilters = Omit<ApprovalFilters, 'status' | 'search'> & { status?: ApprovalStatus };

/**
 * Hook para obtener lista de aprobaciones con filtros
 * ✨ Built with tRPC for full type safety
 */
export function useApprovals(filters?: ApprovalQueryFilters) {
  const result = trpc.approvals.getApprovals.useQuery(filters || {});

  return {
    data: result.data?.approvals || [],
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook para obtener una aprobación por ID
 * ✨ Built with tRPC for full type safety
 */
export function useApproval(approvalId?: string) {
  return trpc.approvals.getApproval.useQuery(approvalId!, {
    enabled: !!approvalId,
  });
}

/**
 * Hook para crear una nueva aprobación
 * ✨ Built with tRPC for full type safety
 */
export function useCreateApproval() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.approvals.createApproval.useMutation({
    onSuccess: () => {
      utils.approvals.getApprovals.invalidate();
      toast.success(t('hooks.approvals.aprobacion_creada_exitosamente'));
    },
    onError: (error) => {
      console.error('Error creating approval:', error);
      toastError(error, 'common.toast.error_al_crear_aprobacion');
    },
  });
}

/**
 * Hook para confirmar que SE necesita computadora (DRAFT → PENDING)
 * ✨ Built with tRPC for full type safety
 */
export function useConfirmComputerNeeded() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.approvals.confirmComputerNeeded.useMutation({
    onSuccess: () => {
      utils.approvals.getApprovals.invalidate();
      toast.success(t('hooks.approvals.solicitud_enviada_a_finance_para_aprobacion'));
    },
    onError: (error) => {
      console.error('Error confirming computer need:', error);
      toastError(error, 'common.toast.error_al_confirmar_necesidad_de_equipo');
    },
  });
}

/**
 * Hook para confirmar que NO se necesita computadora (DRAFT → CANCELLED)
 * ✨ Built with tRPC for full type safety
 */
export function useConfirmNoComputerNeeded() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.approvals.confirmNoComputerNeeded.useMutation({
    onSuccess: () => {
      utils.approvals.getApprovals.invalidate();
      toast.success(t('hooks.approvals.solicitud_cancelada_no_se_requiere_equipo'));
    },
    onError: (error) => {
      console.error('Error confirming no computer needed:', error);
      toastError(error, 'common.toast.error_al_cancelar_solicitud');
    },
  });
}

/**
 * Hook para aprobar una compra (PENDING → APPROVED)
 * ✨ Built with tRPC for full type safety
 */
export function useApprove() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.approvals.approve.useMutation({
    onSuccess: () => {
      utils.approvals.getApprovals.invalidate();
      utils.notifications.getNotifications.invalidate();
      toast.success(t('hooks.approvals.compra_aprobada_exitosamente'));
    },
    onError: (error) => {
      console.error('Error approving purchase:', error);
      toastError(error, 'common.toast.error_al_aprobar_compra');
    },
  });
}

/**
 * Hook para rechazar una compra (PENDING → REJECTED)
 * ✨ Built with tRPC for full type safety
 */
export function useReject() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.approvals.reject.useMutation({
    onSuccess: () => {
      utils.approvals.getApprovals.invalidate();
      utils.notifications.getNotifications.invalidate();
      toast.success(t('hooks.approvals.compra_rechazada'));
    },
    onError: (error) => {
      console.error('Error rejecting purchase:', error);
      toastError(error, 'common.toast.error_al_rechazar_compra');
    },
  });
}

/**
 * Hook para marcar como comprado (APPROVED → PURCHASED)
 * ✨ Built with tRPC for full type safety
 */
export function useMarkPurchased() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.approvals.markPurchased.useMutation({
    onSuccess: () => {
      utils.approvals.getApprovals.invalidate();
      utils.notifications.getNotifications.invalidate();
      toast.success(t('hooks.approvals.compra_marcada_como_completada'));
    },
    onError: (error) => {
      console.error('Error marking as purchased:', error);
      toastError(error, 'common.toast.error_al_marcar_como_comprado');
    },
  });
}

/**
 * Hook para sincronizar ingresos futuros desde Google Sheets
 * Crea approvals en DRAFT para altas con fecha_ingreso >= hoy
 * ✨ Built with tRPC for full type safety
 */
export function useQuipteamsKits(enabled = false) {
  return trpc.approvals.getQuipteamsKits.useQuery(undefined, { enabled });
}

export function useQuipteamsOffices(enabled = false) {
  return trpc.approvals.getQuipteamsOffices.useQuery(undefined, {
    enabled,
    staleTime: 10 * 60 * 1000, // offices rarely change
  });
}

/**
 * Estado de todas las cotizaciones de la pantalla en UNA llamada. Antes cada fila
 * pedía el suyo y QuipTeams respondía 429 a la ráfaga (17/09); el backend las
 * consulta de a una con pausa y cache.
 */
export function useQuipteamsOrderStatuses(quoteIds: string[]) {
  return trpc.approvals.getQuipteamsOrderStatuses.useQuery(quoteIds, {
    enabled: quoteIds.length > 0,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
}

export function useOrderFromQuipteams() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.approvals.orderFromQuipteams.useMutation({
    onSuccess: () => {
      utils.approvals.getApprovals.invalidate();
      utils.notifications.getNotifications.invalidate();
      toast.success(t('hooks.approvals.pedido_enviado_a_quipteams'));
    },
    onError: (error) => {
      toastError(error, 'common.toast.error_al_crear_el_pedido_en_quipteams');
    },
  });
}

export function useCancelApproval() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.approvals.cancel.useMutation({
    onSuccess: () => {
      utils.approvals.getApprovals.invalidate();
      toast.success(t('hooks.approvals.solicitud_cancelada_equipo_recuperado'));
    },
    onError: (error) => {
      toastError(error, 'common.toast.error_al_cancelar_la_solicitud');
    },
  });
}

export function useSyncFutureHires() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.approvals.syncFutureHires.useMutation({
    onSuccess: (data) => {
      utils.approvals.getApprovals.invalidate();
      utils.notifications.getNotifications.invalidate();

      if (data.created > 0) {
        toast.success(`${data.created} ingresos futuros sincronizados`);
      } else if (data.skipped > 0) {
        toast.info(t('hooks.approvals.no_hay_nuevos_ingresos_futuros_para'));
      }

      if (data.errors.length > 0) {
        toast.warning(`${data.errors.length} errores durante la sincronización`);
      }
    },
    onError: (error) => {
      console.error('Error syncing future hires:', error);
      toastError(error, 'common.toast.error_al_sincronizar_ingresos_futuros');
    },
  });
}
